/*
 * Talks to the Tangerine V2 gateway's AggregatorController only (POST /fitness/optin, /fitness/optin-confirm,
 * /fitness/kick, /fitness/anti-fraud). Field names below mirror the gateway's OptInDto/OptInConfirmDto/
 * UnsubscribeDto exactly - the gateway sets "service" to "CF" itself, so it is never sent from here.
 */

const buildPParams = (params) =>
    Array.from({ length: 15 }).reduce((acc, _, i) => {
        acc[`p${i + 1}`] = params[`p${i + 1}`] || null;
        return acc;
    }, {});

let injectedScriptEl = null;

const injectScript = (scriptContent, nonce) => {
    const scriptTag = document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.textContent = scriptContent;
    if (nonce) {
        scriptTag.setAttribute('nonce', nonce);
    }
    document.head.appendChild(scriptTag);
    injectedScriptEl = scriptTag;

    document.dispatchEvent(new Event('DCBProtectRun'));
};

let antiFraudScriptLoaded = false;

class ApiSender {
    async optIn(msisdn, language, correlatorId, landingPageUrl, landingPageName, paramsObject = {}) {
        const baseUrl = process.env.REACT_APP_GTW_BASE_URL;
        const payload = {
            msisdn,
            language,
            marketingUuid: paramsObject.campaignId || null,
            gclid: paramsObject.gclid || null,
            gbraid: paramsObject.gbraid || null,
            wbraid: paramsObject.wbraid || null,
            affId: paramsObject.trackerId || null,
            correlatorId,
            landingPageUrl,
            landingPageName,
            ...buildPParams(paramsObject),
        };

        try {
            const response = await fetch(`${baseUrl}/fitness/optin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok) {
                return { success: false, message: result.message, arMessage: result.arMessage };
            }

            return { success: result.status || false, message: result.message, arMessage: result.arMessage, redirectUrl: result.data || null };
        } catch (error) {
            console.error('Error optin:', error.message);
            return { success: false, message: 'Error Happened', arMessage: 'حدث خطأ' };
        }
    }

    async optInConfirm(msisdn, language, otp, otpId, correlatorId, landingPageUrl, landingPageName, paramsObject = {}) {
        const baseUrl = process.env.REACT_APP_GTW_BASE_URL;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const payload = {
            msisdn,
            marketingId: paramsObject.campaignId || null,
            affId: paramsObject.trackerId || null,
            otp,
            language,
            otpId,
            // same correlatorId optIn already received from the anti-fraud check (via the gateway's
            // OptInConfirmDto.correlatorId field) - lets core tie the opt-in-confirm call it makes to
            // integration back to the same id that started this subscription journey
            correlatorId,
            gclid: paramsObject.gclid || null,
            gbraid: paramsObject.gbraid || null,
            wbraid: paramsObject.wbraid || null,
            landingPageUrl,
            landingPageName,
            ...buildPParams(paramsObject),
            timezone,
        };

        try {
            const response = await fetch(`${baseUrl}/fitness/optin-confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok) {
                return { success: false, message: result.message, arMessage: result.arMessage };
            }

            return { success: result.status || false, message: result.message, arMessage: result.arMessage };
        } catch (error) {
            console.error('Error optin confirm:', error.message);
            return { success: false, message: 'Error Happened', arMessage: 'حدث خطأ' };
        }
    }

    // reports Google-Ads click/conversion params to the Google tracking service (fire-and-forget, no caller depends on the result)
    async saveHistory(landingPageUrl, paramsObject = {}) {
        const isGoogleCampaign = ['gclid', 'gbraid', 'wbraid'].some((key) => paramsObject?.hasOwnProperty(key));
        if (!isGoogleCampaign) {
            return;
        }

        const baseUrl = process.env.REACT_APP_GOOGLE_BASE_URL;
        // fields the gateway already tracks explicitly below - anything else in paramsObject is reported as extraParams
        const trackedKeys = ['gclid', 'gbraid', 'wbraid', 'campaignId', 'conversions', 'cost_micros', 'cpa', 'utm_source', 'utm_medium',
            'utm_campaign', 'utm_term', 'utm_content', 'ad_group_id', 'ad_group_name', 'ad_id', 'keyword_id', 'keyword_text', 'match_type',
            'clicks', 'impressions', 'ctr', 'average_cpc', 'conversion_date', 'click_date', 'geo_location', 'device'];
        const extraParams = Object.keys(paramsObject).reduce((acc, key) => {
            if (!trackedKeys.includes(key)) {
                acc[key] = paramsObject[key];
            }
            return acc;
        }, {});

        try {
            await fetch(`${baseUrl}/google/visit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gclid: paramsObject.gclid,
                    gbraid: paramsObject.gbraid,
                    wbraid: paramsObject.wbraid,
                    campaign: paramsObject.campaignId,
                    conversions: paramsObject.conversions,
                    costMicros: paramsObject.cost_micros,
                    cpa: paramsObject.cpa,
                    utmSource: paramsObject.utm_source,
                    utmMedium: paramsObject.utm_medium,
                    utmCampaign: paramsObject.utm_campaign,
                    utmTerm: paramsObject.utm_term,
                    utmContent: paramsObject.utm_content,
                    adGroupId: paramsObject.ad_group_id,
                    adGroupName: paramsObject.ad_group_name,
                    adId: paramsObject.ad_id,
                    keywordId: paramsObject.keyword_id,
                    keywordText: paramsObject.keyword_text,
                    matchType: paramsObject.match_type,
                    clicks: paramsObject.clicks,
                    impressions: paramsObject.impressions,
                    ctr: paramsObject.ctr,
                    averageCpc: paramsObject.average_cpc,
                    conversionDate: paramsObject.conversion_date,
                    clickDate: paramsObject.click_date,
                    geoLocation: paramsObject.geo_location,
                    device: paramsObject.device,
                    landingPageUrl,
                    extraParams: JSON.stringify(extraParams),
                }),
            });
        } catch (error) {
            console.error('Error save history:', error.message);
        }
    }

    async unsub(msisdn) {
        const baseUrl = process.env.REACT_APP_GTW_BASE_URL;

        try {
            const response = await fetch(`${baseUrl}/fitness/kick`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ msisdn }),
            });

            const result = await response.json();

            if (!response.ok) {
                return { success: false, message: result.message, arMessage: result.arMessage };
            }

            return { success: result.status || false, message: result.message, arMessage: result.arMessage };
        } catch (error) {
            console.error('Error unsub:', error.message);
            return { success: false, message: 'Error Happened', arMessage: 'حدث خطأ' };
        }
    }

    // fetches the aggregator's own anti-fraud script/CSP nonce (gateway POST /fitness/anti-fraud) and injects it once.
    // Gated server-side to DU subscribers: msisdn is passed along in case it's already been typed in, as a fallback
    // for when the IP alone (e.g. on WiFi) can't identify the operator.
    async loadExternalScript(msisdn) {
        try {
            if (antiFraudScriptLoaded) {
                return { correlatorId: null };
            }

            if (['/unsub', '/terms', '/privacy', '/success'].includes(window.location.pathname)) {
                return { correlatorId: null };
            }

            const baseUrl = process.env.REACT_APP_GTW_BASE_URL;
            const response = await fetch(`${baseUrl}/fitness/anti-fraud`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ msisdn: msisdn || null }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch the anti-fraud script: ${response.statusText}`);
            }

            const result = await response.json();
            if (result?.code === 'SUCCESS' && result?.jsString && result?.nonce) {
                injectScript(result.jsString, result.nonce);
                antiFraudScriptLoaded = true;
            }

            return { correlatorId: result?.correlatorId || null };
        } catch (error) {
            console.error('Error loading anti-fraud script:', error.message);
            return { correlatorId: null };
        }
    }

    // Removes the anti-fraud <script> tag this page injected and resets the "already loaded" flag, so a
    // later DU candidate (e.g. after the user deletes/changes the number they typed) can cleanly trigger a
    // fresh check instead of being silently short-circuited by antiFraudScriptLoaded. Only the tag itself
    // is removed - any event listeners the third-party script registered on its own (on document, window,
    // etc.) when it ran are outside our control and can't be un-registered from here; it exposes no
    // teardown hook. No-op if nothing was ever injected.
    resetAntiFraudScript() {
        if (injectedScriptEl) {
            injectedScriptEl.remove();
            injectedScriptEl = null;
        }
        antiFraudScriptLoaded = false;
    }
}

export default ApiSender;
